import { Fragment, type ReactNode } from 'react';
import { useI18n } from '@/hooks/useI18n';
import { NotificationTemplateMapper } from '@/services/notifications/NotificationTemplateMapper';
import {
  PREVIEW_SAMPLE_PAYLOAD,
  samplePreviewValue,
} from '@/lib/admin/email-templates/constants/preview-samples';

const PLACEHOLDER = /\{(\w+)\}/g;

/**
 * Sostituisce i `{placeholder}` con valori campione, evidenziandoli: l'admin
 * vede il testo com'è ma capisce a colpo d'occhio quali pezzi sono dinamici.
 */
const renderWithSamples = (text: string, highlightClassName: string): ReactNode => {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  PLACEHOLDER.lastIndex = 0;
  while ((match = PLACEHOLDER.exec(text)) !== null) {
    if (match.index > lastIndex) nodes.push(text.slice(lastIndex, match.index));
    nodes.push(
      <span key={`${match.index}-${match[1]}`} className={highlightClassName}>
        {samplePreviewValue(match[1])}
      </span>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));

  return nodes.map((node, index) => <Fragment key={index}>{node}</Fragment>);
};

const PreviewFrame = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="flex flex-col gap-1.5">
    <div className="flex items-center gap-2">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
        {label}
      </span>
      <span className="h-px flex-1 bg-gray-200" />
    </div>
    {children}
  </div>
);

/** Anteprima dell'oggetto email, come riga in posta in arrivo. */
export const EmailSubjectPreview = ({ subject }: { subject: string }) => {
  const { t } = useI18n();

  return (
    <PreviewFrame label={t('emailTemplates.preview.label')}>
      <div className="flex items-center gap-2.5 rounded-lg border border-gray-200 bg-white px-3 py-2.5">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-900 text-[10px] font-bold text-white">
          P2
        </span>
        <span className="flex min-w-0 flex-col">
          <span className="text-[11px] text-gray-500">
            {t('emailTemplates.preview.emailFrom', {
              recipient: samplePreviewValue('client_first_name'),
            })}
          </span>
          <span className="break-words text-sm font-semibold text-gray-900">
            {subject.trim()
              ? renderWithSamples(subject, 'rounded bg-blue-50 px-1 font-medium text-blue-700')
              : t('emailTemplates.preview.emptySubject')}
          </span>
        </span>
      </div>
    </PreviewFrame>
  );
};

/**
 * Anteprima della notifica in-app.
 *
 * Titolo e messaggio arrivano dalle TRADUZIONI (`notifications.templates.<key>`),
 * non da `notification_message`: è così che il campanello le rende a runtime, e
 * mostrarlo qui evita di credere che il testo scritto in questa scheda sia quello
 * che il destinatario leggerà.
 */
export const NotificationToastPreview = ({ templateKey }: { templateKey: string }) => {
  const { t } = useI18n();
  // Con una chiave sconosciuta il mapper logga un warning a ogni render: qui
  // passiamo direttamente null, che è la stessa cosa (fallback) ma silenziosa.
  const isKnown = NotificationTemplateMapper.isKnownTemplate(templateKey || null);
  const mapping = NotificationTemplateMapper.mapTemplate(
    isKnown ? templateKey : null,
    PREVIEW_SAMPLE_PAYLOAD,
    t
  );

  return (
    <PreviewFrame label={t('emailTemplates.preview.label')}>
      <div className="flex items-start gap-2.5 rounded-lg border border-gray-200 bg-white px-3 py-2.5 shadow-sm">
        <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-red-500" aria-hidden />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="text-sm font-semibold text-gray-900">{mapping.title}</span>
          <span className="break-words text-xs text-gray-600">{mapping.message}</span>
          <span className="mt-1 text-[10px] text-gray-400">
            {t('emailTemplates.preview.justNow')}
          </span>
        </span>
      </div>
      <p className="text-[11px] text-gray-400">
        {isKnown
          ? t('emailTemplates.preview.notificationFromI18n')
          : t('emailTemplates.preview.notificationFallback')}
      </p>
    </PreviewFrame>
  );
};
